import React from 'react';
import { cn } from '../../../utils/cn';

// Card variants based on your existing patterns
const cardVariants = {
  // Based on your .device-card
  default: 'bg-white border border-gray-200 shadow-card hover:shadow-cardHover',
  
  // Device card when online
  online: 'bg-white border border-secondary-500 shadow-card hover:shadow-cardHover',
  
  // Elevated card
  elevated: 'bg-white border-0 shadow-medium hover:shadow-strong',
  
  // Flat card
  flat: 'bg-gray-50 border border-gray-200',
  
  // Interactive card
  interactive: 'bg-white border border-gray-200 shadow-card hover:shadow-cardHover hover:-translate-y-0.5 cursor-pointer'
};

const cardSizes = {
  sm: 'p-3',              // Small padding
  base: 'p-4',            // Standard padding (16px)
  lg: 'p-5',              // Large padding (20px) 
  xl: 'p-6'               // Extra large padding
};

const Card = React.forwardRef(({
  children,
  className,
  variant = 'default',
  size = 'base',
  rounded = true,
  clickable = false,
  loading = false,
  header,
  footer,
  ...props
}, ref) => {
  const baseStyles = 'flex flex-col transition-all duration-base overflow-hidden relative';
  const variantStyles = cardVariants[variant] || cardVariants.default;
  const sizeStyles = cardSizes[size] || cardSizes.base;
  const roundedStyles = rounded ? 'rounded-card' : '';
  const clickableStyles = clickable ? 'cursor-pointer hover:-translate-y-0.5' : '';
  
  return (
    <div
      ref={ref}
      className={cn(
        baseStyles,
        variantStyles,
        roundedStyles,
        clickableStyles,
        !header && !footer && sizeStyles, // Only apply padding if no header/footer
        className
      )}
      {...props}
    >
      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      
      {/* Header */}
      {header && (
        <div className={cn('border-b border-gray-100', sizeStyles)}>
          {header}
        </div>
      )}
      
      {/* Content */}
      <div className={cn('flex-1', header || footer ? sizeStyles : '')}>
        {children}
      </div>
      
      {/* Footer */}
      {footer && (
        <div className={cn('border-t border-gray-100 mt-auto', sizeStyles)}>
          {footer}
        </div>
      )}
    </div>
  );
});

Card.displayName = 'Card';

export default Card;